import {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from "react";
import { DEFAULT_THEME } from "../lib/config";

type Theme = "dark" | "light";

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setTheme] = useState<Theme>(DEFAULT_THEME);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        const savedTheme = localStorage.getItem(
            "acetrials_ai_theme",
        ) as Theme | null;
        if (savedTheme) {
            setTheme(savedTheme);
        }
    }, []);

    useEffect(() => {
        if (mounted && theme) {
            localStorage.setItem("acetrials_ai_theme", theme);
            if (theme === "light") {
                document.documentElement.classList.add("light");
                document.documentElement.classList.remove("dark");
            } else {
                document.documentElement.classList.add("dark");
                document.documentElement.classList.remove("light");
            }
        }
    }, [theme, mounted]);

    const toggleTheme = () => {
        setTheme((prev) => (prev === "dark" ? "light" : "dark"));
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
}
